import { useCallback, useEffect, useMemo, useState } from "react";
import { Post } from "@/src/models/post.model";
import { usePostStickers } from "./use-post-stickers";
import { stickerModeStore, useStickerModeActive } from "@/src/components/posts/stickers/sticker-mode-store";
import { clamp, isImageUrl, ROT, round, SCALE, StickerDraft } from "@/src/components/posts/stickers/sticker-geometry";

/** mediaIndex for a sticker placed on the card body of a post without images; x/y are fractions of that box. */
export const CARD_TARGET = -1;

/**
 * In-place sticker mode for one post card: which image is targeted, the
 * viewer's editable sticker (draft) and the focused sticker (who placed it).
 */
export const useStickerSession = (post: Post) => {
  const active = useStickerModeActive(post.ItemId);
  const stickers = usePostStickers(post);
  const { mine, place } = stickers;
  const attachments = post.attachmentUrls ?? [];
  const imageIndexes = useMemo(
    () => attachments.map((u, i) => (isImageUrl(u) ? i : -1)).filter((i) => i >= 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attachments.join("|")],
  );
  const [mediaIndex, setMediaIndex] = useState(0);
  const [draft, setDraft] = useState<StickerDraft | null>(null);
  const [focused, setFocused] = useState<string | null>(null);

  const fromMine = useCallback(
    (index: number): StickerDraft | null =>
      mine && (mine.mediaIndex === index || (index === CARD_TARGET && !imageIndexes.length))
        ? { stickerId: mine.stickerId, x: mine.x, y: mine.y, rotation: mine.rotation, scale: mine.scale }
        : null,
    [mine, imageIndexes],
  );

  // Entering the mode targets the image that carries my sticker (or the first image).
  useEffect(() => {
    if (!active) { setDraft(null); setFocused(null); return; }
    // Posts without images (text, link/X previews) target the whole card body.
    const start = !imageIndexes.length ? CARD_TARGET : mine && imageIndexes.includes(mine.mediaIndex) ? mine.mediaIndex : imageIndexes[0];
    setMediaIndex(start);
    setDraft(fromMine(start));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const dirty = !!draft && (() => {
    const m = fromMine(mediaIndex);
    return !m || m.stickerId !== draft.stickerId || m.x !== draft.x || m.y !== draft.y || m.rotation !== draft.rotation || m.scale !== draft.scale;
  })();

  const pick = (stickerId: string) =>
    setDraft((d) => (d ? { ...d, stickerId } : { stickerId, x: 0.5, y: 0.5, rotation: 0, scale: 1 }));
  const rotateBy = (dir: 1 | -1) =>
    setDraft((d) => d && { ...d, rotation: clamp(d.rotation + dir * ROT.step, ROT.min, ROT.max) });
  const scaleBy = (dir: 1 | -1) =>
    setDraft((d) => d && { ...d, scale: round(clamp(d.scale + dir * SCALE.step, SCALE.min, SCALE.max), 2) });
  const goTo = (index: number) => {
    setMediaIndex(index);
    setFocused(null);
    // Keep an unsaved draft when switching image: saving moves it there.
    setDraft((d) => (dirty && d ? d : fromMine(index)));
  };
  const save = async () => {
    if (!draft || !dirty) return;
    await place({ ...draft, mediaIndex });
  };
  const exit = () => stickerModeStore.set(null);
  const done = async () => {
    const pendingSave = dirty ? save() : undefined;
    exit();
    await pendingSave;
  };
  const toggle = () => stickerModeStore.toggle(post.ItemId);

  return {
    ...stickers,
    active, toggle, exit, done,
    mediaIndex, imageIndexes, goTo,
    draft, setDraft, dirty, pick, rotateBy, scaleBy, save,
    focused, setFocused,
  };
};

export type StickerSession = ReturnType<typeof useStickerSession>;
